import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getAccountSettings } from "../api/accountSettings";
import { getClientByCode, normalizeClientCode } from "../api/clientAccess";
import { resolveAdvisorForClient } from "../api/proIdentity";
import { listTable } from "../db";

const STORE = "dh_client_portal";

export function agentIdForClient(client) {
  if (!client?.id) return null;
  const open = listTable("transactions").find(
    (row) => row.client_id === client.id && row.status === "open" && row.agent_id
  );
  return open?.agent_id || null;
}

export function writeClientPortal(client) {
  try {
    sessionStorage.setItem(
      STORE,
      JSON.stringify({
        id: client.id,
        code: client.client_code,
        type: client.role_type,
        name: client.name,
        agentId: agentIdForClient(client),
      })
    );
  } catch {
    /* ignore */
  }
}

export function readClientPortal() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE) || "null");
  } catch {
    return null;
  }
}

export function clearClientPortal() {
  try {
    sessionStorage.removeItem(STORE);
  } catch {
    /* ignore */
  }
}

function emptyPortalSettings() {
  return {
    advisorLabel: "Your Advisor",
    showPhone: true,
    showEmail: true,
    showOffice: true,
    allowMessages: true,
    allowUploads: true,
    showProgress: true,
    showTips: true,
  };
}

export function useClientPortal() {
  const { pathname, search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    const fromQuery = normalizeClientCode(params.get("client") || "");
    const stored = readClientPortal();
    const reserved = /^(home|login|welcome|property-details|interior-features|exterior-features|upgrades|condition-notes|photos|improvements|listing-readiness|tips|notes|portfolio-summary)$/i;
    const pathMatch = pathname.match(/^\/(?:portal\/)?(buyer|seller)\/([^/]+)/i);
    const fromPath = pathMatch && !reserved.test(pathMatch[2]) ? pathMatch[2] : "";
    const code = fromQuery || fromPath || stored?.code || "";
    const resolved = code ? getClientByCode(code) : null;
    if (!resolved && !stored?.id) {
      return { active: false, client: null, advisor: null, settings: emptyPortalSettings() };
    }
    const client = resolved || {
      id: stored.id,
      name: stored.name,
      client_code: stored.code,
      role_type: stored.type,
    };
    const agentId = resolved ? agentIdForClient(resolved) : stored?.agentId;
    const advisor = resolveAdvisorForClient(agentId);
    const settings = agentId ? getAccountSettings(agentId).portal : emptyPortalSettings();
    return {
      active: Boolean(resolved || (stored && (fromQuery || fromPath || search.includes("portal=1")))),
      client,
      advisor,
      settings,
    };
  }, [pathname, search]);
}
